"use client";
import React, { useEffect } from "react";
import Image from "next/image";
import {
    FaTimes,
    FaChevronLeft,
    FaChevronRight,
    FaHeart,
} from "react-icons/fa";
import { isImage } from "~/utils/images";
import { If } from "../v3/Conditional";
import { H3 } from "../themed/font";
import { cn } from "~/utils/fontsConfig";

type ImageModalProps = {
    src: string;
    alt: string;
    title?: string;
    onNext: () => void;
    onPrevious: () => void;
    onClose: () => void;
    isLiked?: boolean;
    onLike?: (newValue: boolean) => void;
};

const ImageModal: React.FC<ImageModalProps> = ({
    src,
    alt,
    title,
    onNext,
    onPrevious,
    onClose,
    isLiked,
    onLike,
}) => {
    const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case "ArrowLeft":
                onPrevious();
                break;
            case "ArrowRight":
                onNext();
                break;
            case "Escape":
                onClose();
                break;
        }
    };

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onNext, onPrevious, onClose]);

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75"
            onClick={onClose}
        >
            <div
                className="relative flex w-full max-w-4xl items-center justify-center p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    className="absolute right-2 top-20 z-[10000] cursor-pointer rounded-full bg-black/50 p-2 text-2xl text-white hover:bg-black/80 hover:text-gray-300 md:right-2 md:top-8"
                    onClick={onClose}
                >
                    <FaTimes />
                </button>

                <FaChevronLeft
                    className="absolute left-4 z-[10000] cursor-pointer text-3xl text-white hover:text-gray-300 md:text-4xl"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPrevious();
                    }}
                />
                <div className="relative h-[calc(100vh-8rem)] w-full">
                    <If condition={isImage(src)}>
                        <Image
                            src={src}
                            alt={alt}
                            fill
                            style={{ objectFit: "contain" }}
                            className="max-h-full max-w-full"
                        />
                    </If>
                    <If condition={!isImage(src)}>
                        <div className="flex h-full flex-col items-center justify-center">
                            No preview available for non-images
                        </div>
                    </If>
                </div>
                <FaChevronRight
                    className="absolute right-4 z-[10000] cursor-pointer text-3xl text-white hover:text-gray-300 md:text-4xl"
                    onClick={(e) => {
                        e.stopPropagation();
                        onNext();
                    }}
                />

                {title && (
                    <H3 className="absolute top-8 cursor-pointer bg-black/50 px-4 text-2xl text-gray-300">
                        {title}
                    </H3>
                )}
                {onLike && (
                    <FaHeart
                        className={cn(
                            "absolute bottom-4 right-4 z-[10000] text-4xl",
                            "cursor-pointer",
                            isLiked
                                ? "text-v3-primary hover:text-v3-primary/50"
                                : "text-white hover:text-v3-primary",
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            onLike(!isLiked);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default ImageModal;
